import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import { normaliseOnboardingState, UNAVAILABLE_STATE, type OnboardingState, type OnboardingUpdate } from './types';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const onboardingKeys = {
  state: ['users', 'me', 'onboarding'] as const,
};

/**
 * The caller's onboarding state. Any failure, including a 404 from a backend
 * that predates the endpoint, resolves to an `exempt` state flagged
 * `unavailable`: the gate never blocks the app on it, and an open wizard
 * never plans its next step from it.
 */
export function useOnboardingState(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: onboardingKeys.state,
    enabled: options.enabled ?? true,
    retry: 1,
    staleTime: 60 * 1000,
    queryFn: async ({ signal }): Promise<OnboardingState> => {
      try {
        const response = await axiosInstance.get<ApiEnvelope<OnboardingState>>(apiUrl.users.meOnboarding, { signal });
        return normaliseOnboardingState(response.data?.data);
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }
        console.warn('[first-login] onboarding state unavailable; treating as exempt', error);
        return UNAVAILABLE_STATE;
      }
    },
  });
}

export function useUpdateOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (update: OnboardingUpdate): Promise<OnboardingState> => {
      const response = await axiosInstance.put<ApiEnvelope<OnboardingState>>(apiUrl.users.meOnboarding, update);
      return normaliseOnboardingState(response.data?.data);
    },
    onSuccess: (state) => {
      queryClient.setQueryData(onboardingKeys.state, state);
    },
  });
}
